﻿// implements item bar where overflown items are rendered as a dropdown

import { createElement, uniqueId } from './_dom';
import { registerComponent } from "./component";
import { debounce } from "./debounce";

const DEBUG = false;
const debugLog = (...args) => DEBUG && console.log(...args);

/** 
 * @typedef ItemBarOptions 
 * @property {string} moreTitle
 * @property {string[]} classPrefix
 * @property {(Element, boolean) => void} toggleItemMode
 * @property {() => Element} alwaysKeepItem
 */


class ItemBar {
	/**
	 * @param {Element} element
	 */
	constructor(element) {
		this.#parent = element;
		this.#parentIsList = /UL|OL/.test(element.tagName);
		new ResizeObserver(this.#delayedRunOnResize).observe(element);
	}

	static #toggleItemMode(item, isMenu) {
		item.role = isMenu ? "menuitem" : "none";
	}

	get parent() {
		return this.#parent;
	}

	get itemCells() {
		return [...this.#itemCells];
	}

	get #dropdown() {
		return this.#moreItem.querySelector(`.v3-itembar__menu`);
	}

	get #badge() {
		return this.#moreItem.querySelector(`.v3-itembar__badge`);
	}

	#findCutOff(parentWidth, cutOff) {
		const itemCells = this.#itemCells;
		const itemCount = itemCells.length;
		const widths = this.#itemWidths;
		const gap = this.#itemGap;
		if (!itemCount) return 0;

		debugLog('ITEMBAR: width: ', parentWidth, 'moreItem', this.#moreItemWidth, ' cut off at ', cutOff);
		let i = itemCount;
		for (let j = 0, p = -gap; j < itemCount; j++) {
			p += gap + widths[j];
			debugLog('ITEMBAR: [', j, ']=', itemCells[j].innerText, ' right at ', p);
			if (p >= cutOff) {
				// j does not fit before potential [More] button
				i = Math.min(i, j);
				if (p >= parentWidth) {
					// and does not fit to parent -- stop it
					break;
				} else if (j == itemCount - 1) {
					// but it's a last element, let it in
					i = itemCount;
				}
			}
		}
		debugLog('ITEMBAR: cut at ' + i);
		return i;
	}

	#createDropdown() {
		const dropdownId = uniqueId("v3-itembar-dropdown");
		const itembarTrigger = createElement('button', {
				type: "button",
				title: this.#options.moreTitle,
				class: [...this.#options.classPrefix.map(e => `${e}__overflow ${e}__item`), "js-auto-popup", this.#options.badgeClass ],
				"aria-haspopup": "true",
				"aria-controls": dropdownId,
				"data-popper-placement": "bottom-end"
			},
			createElement("span", { class: this.#options.classPrefix.map(e => `${e}__badge`) }, "999"),
			this.#options.moreTitle);

		const itembarDropdown = createElement("ul", {
			id: dropdownId,
			class: [ ...this.#options.classPrefix.map(e => `${e}__menu`), "v3-menu v3-menu-scrollable v3-popup" ],
			role: 'menu',
			tabindex: 0
		});

		const more = createElement(this.#parentIsList ? "li" : "div", {
				role: "none",
				class: this.#options.classPrefix.map(e => `${e}__cell-more`)
			},
			itembarTrigger,
			itembarDropdown
		);
		return more;
	}

	#paintDebug = (cutOff) => {
		const parent = this.#parent;
		const itemCells = this.#itemCells;
		const height = 30;
		
		/** @type {HTMLCanvasElement} */
		let canvas = this.#parent.querySelector(".v3-itembar__debug");
		if (!canvas) {
			canvas = createElement("canvas", { "class": "v3-itembar__debug" });
			parent.append(canvas);
		}
		canvas.width = parent.offsetWidth;
		canvas.height = 30;
		const ctx = canvas.getContext("2d", {});
		ctx.clearRect(0, 0, canvas.width, 100);
		ctx.lineWidth = 1;
		let p1 = -this.#itemGap, p2 = -this.#itemGap;
		for (let i = 0; i < cutOff; i++) {
			const w1 = this.#itemWidths[i];
			const w2 = itemCells[i].getBoundingClientRect().width;
			p1 += this.#itemGap + w1;
			p2 += this.#itemGap + w2;
			ctx.fillStyle = "red";
			ctx.strokeStyle = "red";
			ctx.fillRect(p2, 0, 1, height / 2);
			ctx.moveTo(p2, 0);
			ctx.lineTo(p2,  height / 2);
			ctx.fillStyle = "green";
			ctx.strokeStyle = "green";
			ctx.fillRect(p2,  height / 2, 1,  height / 2);
			ctx.moveTo(p2,  height / 2);
			ctx.lineTo(p2, height);
			debugLog("ITEM BAR ITEM", i, "calc", w1, "@", p1, "real", w2, "@", p2);
		}
	};

	#delayedRunOnResize = debounce(() => this.#resizeSingleRow(), 300);

	#resizeSingleRow = () => {
		// this.#parent.style.overflowX = "";
		const parent = this.#parent;
		const itemCells = this.#itemCells;
		const parentWidth = parent.clientWidth;
		const moreItemSpace = this.#itemGap + this.#moreItemWidth;
		let cutOff = parentWidth - moreItemSpace;
		// debugLog("Parent width", parentWidth, "more width: " + moreItemWidth, "cut off", cutOff);
		const alwaysKeepCell = this.#options.alwaysKeepItem?.()?.parentElement;
		const alwaysKeepCellIndex = alwaysKeepCell ? itemCells.indexOf(alwaysKeepCell) : -1;
		const hasAlwaysKeepItem = alwaysKeepCellIndex !== -1;
		const isNeighborMore = hasAlwaysKeepItem && this.#oldFullWidth > parentWidth && alwaysKeepCell.nextElementSibling === this.#moreItem;
		let cutIndex = this.#findCutOff(parentWidth, cutOff);
		let itemBarCells;
		let menuCells;

		if (alwaysKeepCellIndex > 0 && (cutIndex <= alwaysKeepCellIndex || isNeighborMore)) {
			cutOff -= this.#itemWidths[alwaysKeepCellIndex];
			cutIndex = this.#findCutOff(parentWidth, cutOff);
			itemBarCells = [...itemCells.slice(0, cutIndex), alwaysKeepCell];
			this.#toggleClasses(itemBarCells, false);
			parent.replaceChildren(...itemBarCells);
			menuCells = [...itemCells.slice(cutIndex, alwaysKeepCellIndex), ...itemCells.slice(alwaysKeepCellIndex + 1)];
		} else {
			itemBarCells = [...itemCells.slice(0, cutIndex)];
			this.#toggleClasses(itemBarCells, false);
			parent.replaceChildren(...itemBarCells);
			menuCells = itemCells.slice(cutIndex);
		}
		if (menuCells.length) {
			this.#moreItem.style.visibility = "";
			this.#moreItem.style.display = "";
			this.#badge.textContent = menuCells.length;
			this.#toggleClasses(menuCells, true);
			this.#dropdown.replaceChildren(...menuCells);
			const hasIcons = this.#dropdown.querySelector(`.v3-menu__item[class^="ui-icon-"],.v3-menu__item[class*=" ui-icon-"]`) != null;
			this.#dropdown.classList.toggle("v3-menu--hasicons", hasIcons);
			parent.append(this.#moreItem);
		} else {
			this.#moreItem.style.display = "none";
		}
		this.#oldFullWidth = parentWidth;
		DEBUG && this.#paintDebug(cutIndex);
	};

	#toggleClasses(itemCells, isMenu) {
		itemCells.forEach(item => {
			this.#options.classPrefix.forEach(prefix => item.classList.toggle(`${prefix}__cell`, !isMenu));
			item.classList.toggle("v3-menu__cell", isMenu);
			this.#options.toggleItemMode?.(item, isMenu);
		});
	}

	#measureItems() {
		this.#itemWidths = [...this.#parent.children].map(item => item.getBoundingClientRect().width);
		this.#moreItemWidth = this.#itemWidths.splice(-1, 1)[0];
	}

	forceResize = () => {
		this.#itemGap = parseFloat(window.getComputedStyle(this.#parent).columnGap);
		this.#toggleClasses(this.#itemCells, false);
		this.#parent.replaceChildren(...this.#itemCells);
		this.#parent.append(this.#moreItem);
		this.#moreItem.style.visibility = "hidden";
		this.#moreItem.style.display = "";
		requestAnimationFrame(() => {
			// let it repaint and then measure item widths. Using getBoundingClientRect().width for float results
			this.#measureItems();
			this.#delayedRunOnResize();
		});
	};

	refresh(/** @type {ItemBarOptions}*/ options) {
		const parent = this.#parent;
		if (!parent)
			throw new Error("ItemBar: parent element is not defined");

		if (options) {
			this.#options = { ...this.#defaultOptions, ...options };
			if (!Array.isArray(this.#options.classPrefix))
				this.#options.classPrefix = [this.#options.classPrefix];
			if (!this.#options.classPrefix.includes("v3-itembar"))
				this.#options.classPrefix.push("v3-itembar");
		}

		parent.classList.add(...this.#options.classPrefix);
		const cellClasses = this.#options.classPrefix.map(prefix => `${prefix}__cell`);
		if (this.#parentIsList || this.#parent.firstElementChild?.matches(".v3-itembar__cell")) {
			// if parent is list LI serves as a cell
			this.#itemCells = Array.from(this.#parent.children);
			this.#itemCells.forEach(itemCell => itemCell.classList.add(...cellClasses));
		} else {
			// if parent is not a list, create cells (Action bar)
			const items = Array.from(this.#parent.children);
			this.#itemCells = items.map(item => createElement(this.#parentIsList ? "li" : "div", { "class": cellClasses }, item));
			this.#parent.replaceChildren(...this.#itemCells);
		}
		this.#moreItem = this.#createDropdown();
		this.#moreItem.style.display = 'none';

		const doForcedResizeOnce = () => {
			if (!this.#forcedResizeProcessed) {
				this.#forcedResizeProcessed = true;
				this.forceResize();
			}
		};
		document.fonts.ready.then(doForcedResizeOnce);
		window.addEventListener("load", doForcedResizeOnce);
	}

	get #defaultOptions() {
		return { classPrefix: ["v3-itembar"], moreTitle: "--More--", alwayKeepItem: null, toggleItemMode: ItemBar.#toggleItemMode };
	}

	/** @type {Element} */
	#parent;
	/** @type {boolean}  */
	#parentIsList;
	/** @type {Element[]} */
	#itemCells;
	/** @type {number} */
	#itemGap;
	/** @type {number[]} */
	#itemWidths = [];
	/** @type {number} */
	#moreItemWidth;
	/** @type {Element} */
	#moreItem;
	/** @type {number} */
	#oldFullWidth;
	/** @type {ItemBarOptions}  */
	#options;
	/** @type {boolean} */
	#forcedResizeProcessed;
}

// needs referential integrity
/** 
 * @param {Element} element
 * @param {ItemBarOptions} options
 * @returns {ItemBar}
 */
function createItemBar(element, options) {
	const itemBar = new ItemBar(element);
	itemBar.refresh(options);
	return itemBar;
}

const itembar = registerComponent(createItemBar);

export { itembar, ItemBar };
